import { Typography } from 'antd';
import lrz from 'lrz';
import moment from 'moment';
import { formatStateCode } from './states-list';

export const maskEmail = (email) => {
    const [name, domain] = email.split('@');
    if (!name || !domain) return email; // fallback for invalid format

    const maskedName = name[0] + '***';
    return `${maskedName}@${domain}`;
}

export const maskAccountNumber = (number) => {
    const visibleDigits = 3;
    const maskedSection = '*'.repeat(number.length - visibleDigits);
    const visibleSection = number.slice(-visibleDigits);
    return maskedSection + visibleSection;
}

export const fileToBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
        const canvas = document.createElement('canvas');
        const img = document.createElement('img');
        img.src = reader.result;
        img.onload = () => {
            const ctx = canvas.getContext('2d');
            let originImageWidth = img.width, originImageHeight = img.height;
            let maxHeight = 1024;
            let targetWidth = originImageWidth, targetHeight = originImageHeight;
            if (originImageHeight > maxHeight) {
                targetHeight = maxHeight;
                targetWidth = Math.round(maxHeight * (originImageWidth / originImageHeight));
            }
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            ctx.clearRect(0, 0, targetWidth, targetHeight);
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
            const imageDataURL = canvas.toDataURL(file.type || 'image/jpeg', 1);
            const imageBytes = window.atob(imageDataURL.split(',')[1]);
            const arrayBuffer = new ArrayBuffer(imageBytes.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < imageBytes.length; i++) {
                uint8Array[i] = imageBytes.charCodeAt(i);
            }
            let mimeType = imageDataURL.split(',')[0].match(/:(.*?);/)[1];
            let newFile = new File([uint8Array], file.name, { type: mimeType || 'image/jpeg' });
            var quality = 1;
            if (newFile.size > 1024 * 1024) {
                quality = 0.6;
            }
            lrz(newFile, { quality: quality }).then((rst) => {
                resolve(rst.base64);
            })
        };
    };
    reader.onerror = error => reject(error);
});

export const convertToKB = (sizeInBytes) => {
    const sizeInKB = sizeInBytes / 1024;
    return `${sizeInKB.toFixed(2)} KB`;
}

export const detectChanges = (accessData, tempData) => {
    const changes = {};

    Object.keys(tempData).forEach((key) => {
        if (JSON.stringify(accessData[key]) !== JSON.stringify(tempData[key])) {
            changes[key] = {
                accessData: accessData[key],
                tempData: tempData[key],
            };
        }
    });

    const hasChanges = Object.keys(changes).length > 0;

    return hasChanges;
};

export const getUpdatedFields = (initial, updated) => {
    const changes = {};

    for (const key in updated) {
        if (!initial.hasOwnProperty(key)) {
            changes[key] = updated[key]; // new key added
        } else {
            const initialVal = initial[key];
            const updatedVal = updated[key];

            // Deep compare for nested objects
            if (
                typeof updatedVal === 'object' &&
                updatedVal !== null &&
                !Array.isArray(updatedVal)
            ) {
                const nestedChanges = getUpdatedFields(initialVal, updatedVal);
                if (Object.keys(nestedChanges).length > 0) {
                    changes[key] = nestedChanges;
                }
            } else if (Array.isArray(updatedVal)) {
                // Compare arrays (simple shallow comparison here)
                if (JSON.stringify(initialVal) !== JSON.stringify(updatedVal)) {
                    changes[key] = updatedVal;
                }
            } else {
                // Primitive value change
                if (initialVal !== updatedVal) {
                    changes[key] = updatedVal;
                }
            }
        }
    }

    return changes;
}

export const flattenObject = (obj, parent = '', res = {}) => {
    for (let key in obj) {
        if (!obj.hasOwnProperty(key)) continue;

        let propName = parent ? `${parent}.${key}` : key;
        const value = obj[key];

        if (moment.isMoment(value)) {
            // Moment object => ISO string
            res[propName] = value.toISOString();
        } else if (value instanceof Date) {
            // Native Date => ISO string
            res[propName] = value.toISOString();
        } else if (Array.isArray(value)) {
            // Arrays: send as is (don't flatten inside arrays)
            res[propName] = value;
        } else if (value && typeof value === 'object') {
            // Plain object: recurse deeper
            flattenObject(value, propName, res);
        } else {
            // Primitive value (string, number, boolean, null, undefined)
            res[propName] = value;
        }
    }
    return res;
}

export const parseDob = (utcDateStr) => {
    if (!utcDateStr) return null;
    const utcDate = moment.utc(utcDateStr);
    if (utcDate.hours() >= 12) {
        utcDate.add(1, 'day');
    }
    return utcDate.startOf('day');
};

export const formatPrice = (value) => `$${value.toLocaleString()}`;

export const isAllSuccess = (fileList) => {
    return fileList.every(file => file.status === 'done');
}

export const removeWhiteBackground = (imageUrl) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // Important for CORS if image hosted externally
        img.src = imageUrl;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
                // if white pixel (near white)
                if (data[i] > 240 && data[i + 1] > 240 && data[i + 2] > 240) {
                    data[i + 3] = 0; // set alpha to 0 (transparent)
                }
            }

            ctx.putImageData(imageData, 0, 0);

            // Convert canvas to Blob or dataURL
            canvas.toBlob((blob) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsArrayBuffer(blob);
            }, 'image/png');
        };
    });
}

export const formatWrapAddress = ({ line1, line2 = '', city, state, zip, country = '' }) => {
    const stateCode = formatStateCode(state);
    return (
        <>
            <Typography.Text>{line1}</Typography.Text>
            {
                line2 && (
                    <Typography.Text>{line2}</Typography.Text>
                )
            }
            <Typography.Text>{`${city}, ${stateCode}, ${zip}`}</Typography.Text>
        </>
    )
}
export const formatAddress = ({ line1, line2 = '', city, state, zip, country = '' }) => {
    const stateCode = formatStateCode(state);
    return `${line1}${line2 ? ', ' + line2 : ''}, ${city}, ${stateCode} ${zip}${country ? ', ' + country : ''}`;
};
export const formatPhone = phone => /^\d{10}$/.test(phone) ? phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : phone;
export const formatGender = {
    'male': 'Male',
    'female': 'Female',
}

export const getDefaultMethodObject = (methods) => {
    if (!Array.isArray(methods) || methods.length === 0) {
        return null;
    }

    // Try to find 'direct-deposit'
    const directDeposit = methods.find(item => item.method === 'direct-deposit');
    if (directDeposit) {
        return directDeposit;
    }

    // Try to find 'check-by-mail'
    const checkByMail = methods.find(item => item.method === 'check-by-mail');
    if (checkByMail) {
        return checkByMail;
    }

    // Neither found
    return null;
}

export const getDefaultMethodDescription = (methods) => {
    if (!Array.isArray(methods) || methods.length === 0) {
        return null;
    }

    const directDeposit = methods.find(item => item.method === 'direct-deposit');
    if (directDeposit && directDeposit.directDeposit) {
        const { accountNumber, bankName } = directDeposit.directDeposit;
        const lastFour = accountNumber?.slice(-4) || '****';
        return `[Visa •••• 1234 / ${bankName || 'Bank'} Account ending in ${lastFour}]`;
    }

    const checkByMail = methods.find(item => item.method === 'check-by-mail');
    if (checkByMail && checkByMail.checkByMail) {
        const {
            recipientName = '',
            address1 = '',
            address2 = '',
            city = '',
            state = '',
            zip = ''
        } = checkByMail.checkByMail;

        return <div className='d-flex flex-column mt-2'>
            <Typography.Text>Check by Mail</Typography.Text>
            <div className='d-flex flex-column mt-3'>
                <Typography.Text className='claim-text-label'>Address:</Typography.Text>
                {formatWrapAddress({
                    line1: address1,
                    line2: address2,
                    city: city,
                    state: state,
                    zip: zip,
                })}
            </div>
        </div>
    }

    return null;
}

export const getNonEmptyKeys = (obj) => {
    const result = [];

    Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !(typeof value === 'number' && isNaN(value))
        ) {
            result.push(key);
        }
    });

    return result;
}

export const formatToCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
        return "$ 0.00";
    }
    return `$ ${parseFloat(amount).toFixed(2)}`;
}